import React, { useState, useEffect } from 'react'
import { T, Spinner, Empty, SearchBar, searchMatch, Sheet, TeamLogo } from './ui'
export { TeamLogo }
import { getNFLMatchupMatrix, getNFLMatchupGame } from './api'

/**
 * NFL Matchup Matrix — football-only, no gambling outputs.
 * Renders the weekly matrix of game cards (both teams graded 0-100 + A+..F) and the
 * detailed matchup sheet (comparison bars + scheme/injury detail). Pure rendering of the
 * /nfl/matchup/* API; all scoring lives server-side in backend/nfl/matchup/.
 */

// ── grade colouring (A green → F red) ────────────────────────────────────────
export const gradeCol = g => {
  const l = (g || '')[0]
  return l === 'A' ? T.green : l === 'B' ? T.mint : l === 'C' ? T.amber : l === 'D' ? T.orange : T.red
}
const scoreCol = s => s == null ? T.faint : s >= 78 ? T.green : s >= 62 ? T.mint : s >= 50 ? T.amber : s >= 40 ? T.orange : T.red

function GradeBadge({ score, grade, size = 44 }) {
  const c = gradeCol(grade)
  return (
    <div style={{ flexShrink: 0, width: size, height: size, borderRadius: 11, display: 'grid', placeItems: 'center',
      background: `${c}1A`, border: `1px solid ${c}55` }}>
      <div style={{ fontFamily: T.mono, fontWeight: 900, fontSize: size * 0.4, color: c, lineHeight: 1 }}>{grade || '—'}</div>
      <div style={{ fontSize: size * 0.2, color: T.muted, fontFamily: T.mono, marginTop: 1 }}>{score != null ? score : '—'}</div>
    </div>
  )
}

// A tiny per-category grade pill for the card (P / Run / WR·TE / OL·DL / Def).
function CatPill({ label, val }) {
  const c = scoreCol(val)
  return (
    <div style={{ flex: 1, textAlign: 'center', minWidth: 0 }}>
      <div style={{ fontFamily: T.mono, fontWeight: 800, fontSize: 13, color: c }}>{val != null ? val : '—'}</div>
      <div style={{ fontSize: 10, color: T.muted, letterSpacing: '0.03em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{label}</div>
    </div>
  )
}

const CAT_MINI = [['passing', 'QB'], ['rushing', 'Run'], ['receiving', 'WR/TE'], ['trenches', 'OL/DL'], ['defense', 'Def']]

// One team's row inside a game card.
function TeamRow({ t, opp }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <GradeBadge score={t.overall} grade={t.grade} />
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 4 }}>
          <TeamLogo abbr={t.abbr} size={22} />
          <span style={{ fontSize: 14, fontWeight: 800, color: T.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.name}</span>
        </div>
        {/* Say it at the numbers: these pills are matchup grades vs the opponent, not ratings. */}
        <div style={{ fontSize: 10, color: T.faint, letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: 3 }}>
          Matchup grades{opp ? ` vs ${opp}` : ''}
        </div>
        <div style={{ display: 'flex', gap: 4 }}>
          {CAT_MINI.map(([k, lbl]) => <CatPill key={k} label={lbl} val={t[k]} />)}
        </div>
      </div>
    </div>
  )
}

// ── matrix game card ─────────────────────────────────────────────────────────
export function MatchupGameCard({ game, onOpen }) {
  const { away, home } = game
  const topNotes = [
    ...(home.advantages || []).slice(0, 1).map(x => ({ good: true, team: home.abbr, txt: x })),
    ...(away.advantages || []).slice(0, 1).map(x => ({ good: true, team: away.abbr, txt: x })),
    ...(home.concerns || []).slice(0, 1).map(x => ({ good: false, team: home.abbr, txt: x })),
  ].slice(0, 3)
  const injuries = [...(away.injuries_out || []), ...(home.injuries_out || [])].slice(0, 2)
  return (
    <button onClick={() => onOpen && onOpen(game)} style={{ display: 'block', width: '100%', textAlign: 'left',
      cursor: 'pointer', color: T.text, background: T.card, border: `1px solid ${T.border}`, borderRadius: 12, padding: 14, marginBottom: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <span style={{ fontSize: 10.5, fontWeight: 700, color: T.muted, letterSpacing: '0.04em' }}>
          {away.abbr} @ {home.abbr}{game.gameday ? ` · ${game.gameday.slice(5)}` : ''}
        </span>
        <span style={{ fontSize: 10, color: T.green, fontWeight: 700 }}>Matchup ›</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <TeamRow t={away} opp={home.abbr} />
        <div style={{ height: 1, background: T.border }} />
        <TeamRow t={home} opp={away.abbr} />
      </div>
      {topNotes.length > 0 && (
        <div style={{ marginTop: 11, display: 'flex', flexDirection: 'column', gap: 4 }}>
          {topNotes.map((n, i) => (
            <div key={i} style={{ fontSize: 11, color: n.good ? T.mint : T.amber, display: 'flex', gap: 6, alignItems: 'baseline' }}>
              <span>{n.good ? '▲' : '▽'}</span>
              <span style={{ color: T.muted2 }}><b style={{ color: T.text }}>{n.team}</b> {n.txt}</span>
            </div>
          ))}
        </div>
      )}
      {injuries.length > 0 && (
        <div style={{ marginTop: 8, fontSize: 10.5, color: T.muted, display: 'flex', gap: 5, flexWrap: 'wrap' }}>
          <span style={{ color: T.red, fontWeight: 700 }}>Injuries:</span>{injuries.join(' · ')}
        </div>
      )}
    </button>
  )
}

// ── detail: comparison bars ──────────────────────────────────────────────────
function CompareBar({ label, awayVal, homeVal, awayAbbr, homeAbbr }) {
  const row = (val, abbr, home) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
      <span style={{ width: 34, fontSize: 10, fontWeight: 700, color: T.muted, textAlign: 'right' }}>{abbr}</span>
      <div style={{ flex: 1, height: 12, background: T.card2, borderRadius: 6, overflow: 'hidden' }}>
        <div style={{ width: `${Math.max(0, Math.min(100, val || 0))}%`, height: '100%',
          background: home ? T.green : T.blue, borderRadius: 6, transition: 'width .3s' }} />
      </div>
      <span style={{ width: 26, fontFamily: T.mono, fontWeight: 800, fontSize: 12, color: scoreCol(val) }}>{val != null ? val : '—'}</span>
    </div>
  )
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: T.muted2, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
      {row(awayVal, awayAbbr, false)}
      {row(homeVal, homeAbbr, true)}
    </div>
  )
}

function DetailSide({ side, opp }) {
  const d = side.detail || {}
  const q = side.quality || {}
  const fmtPct = v => v == null ? '—' : `${Math.round(v * 100)}%`
  const g100 = v => v != null ? `${v}/100` : '—'
  // Absolute team-unit quality (league-wide, NOT opponent-adjusted) — the "how good is this
  // unit" grade, so an elite offense reads elite no matter the opponent. Distinct from the
  // opponent-adjusted matchup bars below.
  const qualityRows = [
    ['Passing offense', g100(q.passing)],
    ['Rushing offense', g100(q.rushing)],
    ['Receiving (WR/TE)', g100(q.receiving)],
    ['Pass protection', g100(d.protection)],
    ['Pass rush', g100(d.pass_rush)],
    ['Run defense', g100(d.run_defense)],
    ['Pass defense', g100(d.pass_defense)],
    ['Coverage', g100(d.coverage)],
  ]
  const tendencyRows = [
    ['Primary coverage', d.primary_coverage || '—'],
    ['Man / Zone', d.man_rate != null ? `${fmtPct(d.man_rate)} man · ${fmtPct(d.zone_rate)} zone` : '—'],
    ['Blitz rate', fmtPct(d.blitz_rate)],
  ]
  const c = gradeCol(side.matchup_grade)
  return (
    <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 12, padding: 14, marginBottom: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        <TeamLogo abbr={side.abbr} size={30} />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 15, fontWeight: 800 }}>{side.name}</div>
          <div style={{ fontSize: 10.5, color: T.muted }}>{side.neutral ? 'Neutral site' : side.is_home ? 'Home' : 'Away'}</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontFamily: T.mono, fontWeight: 900, fontSize: 26, color: c, lineHeight: 1 }}>{side.matchup_grade}</div>
          <div style={{ fontFamily: T.mono, fontSize: 12, color: T.muted }}>{side.matchup_score != null ? `${side.matchup_score}/100` : '—'}</div>
        </div>
      </div>
      {(side.advantages || []).length > 0 && (
        <div style={{ marginBottom: 8 }}>
          <div style={{ fontSize: 10, fontWeight: 800, color: T.green, letterSpacing: '0.06em', marginBottom: 4 }}>KEY ADVANTAGES</div>
          {side.advantages.map((a, i) => <div key={i} style={{ fontSize: 11.5, color: T.muted2, marginBottom: 2 }}>▲ {a}</div>)}
        </div>
      )}
      {(side.concerns || []).length > 0 && (
        <div style={{ marginBottom: 8 }}>
          <div style={{ fontSize: 10, fontWeight: 800, color: T.amber, letterSpacing: '0.06em', marginBottom: 4 }}>KEY CONCERNS</div>
          {side.concerns.map((a, i) => <div key={i} style={{ fontSize: 11.5, color: T.muted2, marginBottom: 2 }}>▽ {a}</div>)}
        </div>
      )}
      {(side.injuries_out || []).length > 0 && (
        <div style={{ marginBottom: 8, fontSize: 11, color: T.muted2 }}>
          <span style={{ color: T.red, fontWeight: 700 }}>Injuries: </span>{side.injuries_out.join(' · ')}
        </div>
      )}
      <div style={{ fontSize: 10, color: T.faint, letterSpacing: '0.05em', textTransform: 'uppercase', margin: '8px 0 2px' }}>
        Unit grades · league-wide (not opponent-adjusted)
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 12px', marginTop: 4 }}>
        {qualityRows.map(([k, v]) => (
          <div key={k} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10.5, borderBottom: `1px solid ${T.border}`, padding: '3px 0' }}>
            <span style={{ color: T.muted }}>{k}</span><span style={{ color: T.text, fontWeight: 600, textAlign: 'right' }}>{v}</span>
          </div>
        ))}
      </div>
      <div style={{ fontSize: 10, color: T.faint, letterSpacing: '0.05em', textTransform: 'uppercase', margin: '10px 0 2px' }}>
        Tendencies
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 12px', marginTop: 4 }}>
        {tendencyRows.map(([k, v]) => (
          <div key={k} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10.5, borderBottom: `1px solid ${T.border}`, padding: '3px 0' }}>
            <span style={{ color: T.muted }}>{k}</span><span style={{ color: T.text, fontWeight: 600, textAlign: 'right' }}>{v}</span>
          </div>
        ))}
      </div>
      {(side.scheme_notes || []).length > 0 && (
        <div style={{ marginTop: 8, fontSize: 10.5, color: T.muted }}>{side.scheme_notes.join(' · ')}</div>
      )}
      {side.trace && <ScoreTrace trace={side.trace} components={side.components} />}
    </div>
  )
}

// Traceability: every 0-100 category score expands to the source metrics behind it —
// each input's contribution, or "unavailable" (never a hidden 0). Native <details> so
// it collapses with no JS.
const CAT_LABEL = { passing: 'QB / Passing', rushing: 'Rushing', trenches: 'OL vs DL / Pressure',
  receiving: 'WR / TE', defense: 'Overall Defense' }
function ScoreTrace({ trace, components }) {
  return (
    <details style={{ marginTop: 10 }}>
      <summary style={{ fontSize: 10.5, fontWeight: 700, color: T.green, cursor: 'pointer', letterSpacing: '0.04em' }}>
        How these grades are built
      </summary>
      <div style={{ marginTop: 6 }}>
        {Object.keys(CAT_LABEL).filter(k => trace[k]).map(k => (
          <div key={k} style={{ marginBottom: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10.5, fontWeight: 700, color: T.muted2, marginBottom: 2 }}>
              <span>{CAT_LABEL[k]}</span>
              <span style={{ fontFamily: T.mono, color: scoreCol(components[k]) }}>{components[k] != null ? `${components[k]}/100` : '—'}</span>
            </div>
            {trace[k].map((r, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: r.available ? T.muted : T.faint, padding: '1px 0' }}>
                <span>{r.inverted ? '↧ ' : ''}{r.metric}{r.available && r.raw != null ? ` (${(+r.raw).toFixed(2)})` : ''}</span>
                <span style={{ fontFamily: T.mono }}>{r.available ? r.contribution : 'unavailable'}</span>
              </div>
            ))}
          </div>
        ))}
        <div style={{ fontSize: 10, color: T.faint, marginTop: 4, lineHeight: 1.5 }}>
          ↧ = opponent metric, inverted (a number good for them counts against this team). Each grade is the average of its available inputs; unavailable inputs are dropped, not counted as zero.
        </div>
      </div>
    </details>
  )
}

export function MatchupDetail({ game, season, week, onClose }) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  useEffect(() => {
    let alive = true; setLoading(true)
    getNFLMatchupGame(game.game_id, season, week).then(d => {
      if (!alive) return
      setData(d && d.home ? d : null); setLoading(false)
    })
    return () => { alive = false }
  }, [game.game_id, season, week])

  return (
    <Sheet onClose={onClose}>
      <div style={{ padding: '10px 16px 40px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
          <div style={{ fontSize: 16, fontWeight: 900 }}>{game.away.abbr} @ {game.home.abbr}</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: T.muted, fontSize: 20, cursor: 'pointer' }}>×</button>
        </div>
        <div style={{ fontSize: 11, color: T.muted, marginBottom: 14 }}>Matchup Matrix · Week {week} · on-field analysis (not betting advice)</div>
        {loading ? <Spinner />
          : !data ? <Empty message="Matchup detail unavailable" />
          : (() => {
            const { home, away } = data
            const barLabels = (away.bars || []).map(b => b.label)
            return (
              <>
                {data.neutral_site && (
                  <div style={{ fontSize: 11, color: T.amber, background: T.amberDim, border: `1px solid ${T.amber}44`,
                    borderRadius: 8, padding: '7px 10px', marginBottom: 12 }}>
                    🌐 Neutral / international site — no home-field edge for either team.
                  </div>
                )}
                <DetailSide side={{ ...away, neutral: data.neutral_site }} opp={home} />
                <DetailSide side={{ ...home, neutral: data.neutral_site }} opp={away} />
                <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 12, padding: 14 }}>
                  <div style={{ fontSize: 11, fontWeight: 800, color: T.muted, letterSpacing: '0.06em', marginBottom: 3, textTransform: 'uppercase' }}>Head-to-head</div>
                  <div style={{ fontSize: 10.5, color: T.faint, marginBottom: 12, lineHeight: 1.45 }}>
                    Each team's unit graded <b style={{ color: T.muted2 }}>vs this opponent</b> — higher = more favorable matchup, not a player rating.
                    So a strong offense into a strong defense lands mid-scale; the two teams' bars sum toward a shared matchup, not a talent score.
                  </div>
                  {barLabels.map((lbl, i) => (
                    <CompareBar key={lbl} label={lbl}
                      awayVal={away.bars[i]?.team} homeVal={home.bars[i]?.team}
                      awayAbbr={away.abbr} homeAbbr={home.abbr} />
                  ))}
                </div>
                {(away.quality || home.quality) && (
                  <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 12, padding: 14, marginTop: 12 }}>
                    <div style={{ fontSize: 11, fontWeight: 800, color: T.muted, letterSpacing: '0.06em', marginBottom: 3, textTransform: 'uppercase' }}>Team quality · league-wide</div>
                    <div style={{ fontSize: 10.5, color: T.faint, marginBottom: 12, lineHeight: 1.45 }}>
                      How good each unit is on its own, <b style={{ color: T.muted2 }}>opponent stripped out</b> — this is the talent read.
                      A great QB stays high here even when the matchup above is a wash.
                    </div>
                    {[['QB / Passing', 'passing'], ['Rushing', 'rushing'], ['WR / TE', 'receiving'], ['OL vs DL', 'trenches'], ['Defense', 'defense']].map(([lbl, k]) => (
                      <CompareBar key={k} label={lbl}
                        awayVal={(away.quality || {})[k]} homeVal={(home.quality || {})[k]}
                        awayAbbr={away.abbr} homeAbbr={home.abbr} />
                    ))}
                  </div>
                )}
                <div style={{ fontSize: 10, color: T.faint, marginTop: 12, lineHeight: 1.5 }}>
                  Scores blend prior season and current-season data (Week {week}: {Math.round((data.prior_weight || 0) * 100)}% prior),
                  opponent-adjusted. Overall grades are calibrated across this week's slate — a grade answers
                  "how good is this matchup this week." Higher = more favorable on-field matchup. Informational only.
                </div>
              </>
            )
          })()}
      </div>
    </Sheet>
  )
}

// ── the matrix panel (sorting / filters / selectors) ─────────────────────────
const SORTS = [
  ['spread', 'Biggest mismatch'], ['overall', 'Overall matchup'], ['passing', 'Best passing'],
  ['rushing', 'Best rushing'], ['receiving', 'Best receiving'], ['trenches', 'Best trenches'],
  ['defense', 'Best defense'],
]
const bestOf = (g, key) => key === 'spread'
  ? Math.abs((g.home.overall || 0) - (g.away.overall || 0))
  : Math.max(g.home[key === 'overall' ? 'overall' : key] || 0, g.away[key === 'overall' ? 'overall' : key] || 0)

export function MatchupMatrix({ season, week, weeks = [], onWeek }) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [sample, setSample] = useState(false)
  const [q, setQ] = useState('')
  const [sort, setSort] = useState('spread')
  const [team, setTeam] = useState('ALL')
  const [open, setOpen] = useState(null)

  useEffect(() => {
    let alive = true; setLoading(true)
    getNFLMatchupMatrix(season, week).then(res => {
      if (!alive) return
      setData(res.games || []); setSample(!!res._sample); setLoading(false)
    })
    return () => { alive = false }
  }, [season, week])

  const allTeams = Array.from(new Set((data || []).flatMap(g => [g.away.abbr, g.home.abbr]))).sort()
  let shown = (data || []).filter(searchMatch(q, g => [g.home.abbr, g.home.name, g.away.abbr, g.away.name]))
  if (team !== 'ALL') shown = shown.filter(g => g.home.abbr === team || g.away.abbr === team)
  shown = [...shown].sort((a, b) => bestOf(b, sort) - bestOf(a, sort))

  const selStyle = { background: T.card, color: T.text, border: `1px solid ${T.border}`, borderRadius: 8, padding: '7px 9px', fontSize: 12, fontWeight: 700 }

  return (
    <div style={{ padding: '4px 0 40px' }}>
      {sample && <div style={{ fontSize: 10, color: T.amber, marginBottom: 8, letterSpacing: '0.05em' }}>● Live matchup data unavailable — check back shortly</div>}
      <div style={{ display: 'flex', gap: 6, marginBottom: 8, flexWrap: 'wrap' }}>
        {/* Week selector only when the parent hands one in (onWeek). By default the matrix
            follows the app's single header week, so there's one week source of truth. */}
        {onWeek && (
          <select value={week} onChange={e => onWeek(Number(e.target.value))} style={selStyle} aria-label="Week">
            {(weeks.length ? weeks : Array.from({ length: 18 }, (_, i) => i + 1)).map(w => <option key={w} value={w}>Week {w}</option>)}
          </select>
        )}
        <select value={team} onChange={e => setTeam(e.target.value)} style={selStyle} aria-label="Team">
          <option value="ALL">All teams</option>
          {allTeams.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <select value={sort} onChange={e => setSort(e.target.value)} style={{ ...selStyle, flex: 1 }} aria-label="Sort by">
          {SORTS.map(([k, l]) => <option key={k} value={k}>Sort: {l}</option>)}
        </select>
      </div>
      {data?.length > 0 && <SearchBar value={q} onChange={setQ} placeholder="Search team…" />}
      {data?.length > 0 && (
        <div style={{ fontSize: 10, color: T.faint, margin: '2px 2px 10px', lineHeight: 1.45 }}>
          QB · Run · WR/TE · OL/DL · Def are <b style={{ color: T.muted }}>matchup grades vs this opponent</b>, not player ratings —
          an elite unit into a strong opponent lands mid-scale. Tap a game for each team's league-wide unit grades (e.g. passing offense on its own).
        </div>
      )}
      {loading ? <Spinner />
        : !data?.length ? <Empty message="No games scheduled for this week" />
        : !shown.length ? <Empty message="No games match your filters" />
        : shown.map(g => <MatchupGameCard key={g.game_id} game={g} onOpen={setOpen} />)}
      {open && <MatchupDetail game={open} season={season} week={week} onClose={() => setOpen(null)} />}
    </div>
  )
}
